import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Camera, CheckCheck, ChevronLeft, Loader2, MessageSquare, Send, UserRound, Users, X } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import PeopleApp from "@/components/cef/phonePeople";

/* Messages — real player DMs, groups, and Living Citizens who have your number.
   NPC relationship/favor tools stay available under the People tab instead of
   consuming a separate home-screen icon. */

const iso = (minAgo) => new Date(Date.now() - minAgo * 60000).toISOString();
const DEMO_THREAD = [
  { id: "m1", from_pc: 22110, body: "yo, you voting tonight?", at: iso(40) },
  { id: "m2", from_pc: 37049, body: "yeah, still deciding tbh", at: iso(38) },
  { id: "m3", from_pc: 22110, body: "meet at city hall in 10? polls close soon", at: iso(6) },
];
const DEMO_NPC_THREAD = [
  { id: "n1", from_pc: -1, body: "Saved. It's Emeka — text me when you're around.", at: iso(22) },
  { id: "n2", from_pc: 37049, body: "Will do. Good meeting you today.", at: iso(18) },
];

export default function MessagesApp({ demo, serverId, token, home, contacts, me, accent, sfx, onChanged }) {
  const [tab, setTab] = useState("chats"); // chats | people
  const [view, setView] = useState(null); // dm | group | npc
  const [thread, setThread] = useState([]);
  const [groupMeta, setGroupMeta] = useState(null);
  const [body, setBody] = useState("");
  const [attach, setAttach] = useState("");
  const [picking, setPicking] = useState(false);
  const [grouping, setGrouping] = useState(false);
  const [gName, setGName] = useState("");
  const [gMembers, setGMembers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [snapping, setSnapping] = useState(false);

  const npcThread = (messages) => (messages || []).map((m, i) => ({
    id: `${m.at}-${i}`, from_pc: m.from === "me" ? me.playercode : -1,
    body: m.text, at: m.at,
  }));

  const loadThread = useCallback(() => {
    if (!view) return;
    if (view.kind === "npc") {
      if (demo) { setThread(DEMO_NPC_THREAD); return; }
      api.get(`/nc/servers/${serverId}/life/texts`, { params: { token, ref: view.ref } })
        .then((r) => { setThread(npcThread(r.data.messages)); onChanged(); }).catch(() => {});
      return;
    }
    if (demo) { setThread(view.kind === "dm" && view.pc === 22110 ? DEMO_THREAD : []); return; }
    const url = view.kind === "dm" ? `/nc/servers/${serverId}/phone/messages/${view.pc}` : `/nc/servers/${serverId}/phone/groups/${view.id}/messages`;
    api.get(url, { params: { token } }).then((r) => { setThread(r.data.thread); if (r.data.group) setGroupMeta(r.data.group); if (view.kind === "dm") onChanged(); }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, demo, serverId, token]);

  useEffect(() => {
    loadThread();
    const t = setInterval(loadThread, 3000);
    return () => clearInterval(t);
  }, [loadThread]);

  const send = async () => {
    if (!body.trim() && !attach) return;
    if (view?.kind === "npc" && attach) return toast.error("Citizen texts don't support snaps yet");
    setBusy(true);
    if (demo) {
      setThread((t) => [...t, { id: `local${Date.now()}`, from_pc: me.playercode, body: body.trim(), image_url: attach, at: new Date().toISOString() }]);
      setBody(""); setAttach(""); setBusy(false); sfx.sendSwoosh();
      return;
    }
    try {
      if (view.kind === "npc") {
        await api.post(`/nc/servers/${serverId}/life/text`, { ref: view.ref, token, text: body.trim() });
      } else {
        const payload = view.kind === "dm" ? { to_pc: view.pc, body: body.trim() || " ", image_url: attach } : { body: body.trim(), image_url: attach };
        const url = view.kind === "dm" ? `/nc/servers/${serverId}/phone/messages` : `/nc/servers/${serverId}/phone/groups/${view.id}/messages`;
        await api.post(url, payload, { params: { token } });
      }
      setBody(""); setAttach(""); sfx.sendSwoosh(); loadThread(); onChanged();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const snap = async () => {
    setSnapping(true);
    sfx.shutter();
    if (demo) {
      setTimeout(() => { setAttach("/assets/og-image.png"); setSnapping(false); toast.success("Preview: snap attached"); }, 700);
      return;
    }
    try {
      const r = await api.post(`/nc/servers/${serverId}/phone/shots`, {}, { params: { token } });
      const shotId = r.data.shot.id;
      toast.info(r.data.message);
      const poll = setInterval(async () => {
        try {
          const m = await api.get(`/nc/servers/${serverId}/phone/shots/mine`, { params: { token } });
          const done = m.data.shots.find((s) => s.id === shotId && s.status === "done");
          if (done) { clearInterval(poll); setAttach(done.url); setSnapping(false); toast.success("Snap ready — attached"); }
        } catch {}
      }, 2500);
      setTimeout(() => { clearInterval(poll); setSnapping(false); }, 30000);
    } catch (e) { setSnapping(false); toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const createGroup = async () => {
    if (demo) { toast.success("Preview: group created"); setGrouping(false); return; }
    setBusy(true);
    try {
      const r = await api.post(`/nc/servers/${serverId}/phone/groups`, { name: gName, member_pcs: gMembers }, { params: { token } });
      toast.success(r.data.message); setGrouping(false); setGName(""); setGMembers([]); onChanged();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  if (view) {
    const names = groupMeta?.member_names || {};
    return (
      <div className="h-full flex flex-col">
        <div className="flex items-center gap-2.5 px-4 py-3 border-b border-white/10">
          <button data-testid="thread-back" onClick={() => { sfx.back(); setView(null); onChanged(); }} className="text-white/60 hover:text-white"><ChevronLeft className="w-4 h-4" /></button>
          <span className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">{view.kind === "group" ? <Users className="w-4 h-4" style={{ color: accent }} /> : <UserRound className="w-4 h-4" style={{ color: accent }} />}</span>
          <div><p data-testid="thread-peer" className="font-sans font-semibold text-sm text-white">{view.name}</p><p className="font-mono text-[8px] text-white/50 uppercase">{view.kind === "group" ? `${view.membersCount || ""} members` : view.kind === "npc" ? "Citizen contact" : `#${view.pc}`}</p></div>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3 flex flex-col gap-2" style={{ scrollbarWidth: "thin" }}>
          {thread.length === 0 && <p className="font-mono text-[10px] text-white/40 text-center mt-6">No messages yet — say hey.</p>}
          {thread.map((m) => {
            const mine = m.from_pc === me.playercode;
            return (
              <div key={m.id} className={`max-w-[80%] flex flex-col ${mine ? "self-end items-end" : "self-start items-start"}`}>
                {view.kind === "group" && !mine && <span className="font-mono text-[8px] text-white/40 mb-0.5 ml-1">{names[String(m.from_pc)] || m.from_name}</span>}
                <div className={`px-3.5 py-2.5 rounded-2xl font-mono text-[11px] leading-relaxed ${mine ? "text-black rounded-br-sm" : "bg-white/10 text-white rounded-bl-sm"}`} style={mine ? { backgroundColor: accent } : {}}>
                  {m.image_url && <img src={m.image_url} alt="snap" className="rounded-lg max-w-full mb-1.5 border border-black/20" />}
                  {m.body}
                  <span className={`block text-[7.5px] mt-1 ${mine ? "text-black/60" : "text-white/40"}`}>{String(m.at).slice(11, 16)}{mine && view.kind === "dm" && m.read ? " · read" : ""}</span>
                </div>
              </div>
            );
          })}
        </div>
        {attach && (
          <div className="px-3 pt-2 flex items-center gap-2">
            <img src={attach} alt="snap preview" className="h-14 rounded-lg border border-white/20" />
            <button data-testid="attach-clear" onClick={() => setAttach("")} className="text-white/50 hover:text-white"><X className="w-4 h-4" /></button>
          </div>
        )}
        <div className="flex gap-2 p-3 border-t border-white/10">
          {view.kind !== "npc" && (
            <button data-testid="msg-snap" onClick={snap} disabled={snapping} className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center text-white/70 hover:text-white transition-colors disabled:opacity-40">
              {snapping ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
            </button>
          )}
          <input data-testid="msg-input" value={body} maxLength={500} onChange={(e) => setBody(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Message…"
            className="flex-1 bg-white/10 border border-white/15 rounded-full px-4 py-2.5 font-mono text-[11px] text-white focus:outline-none" />
          <button data-testid="msg-send" onClick={send} disabled={busy || (!body.trim() && !attach)} className="w-10 h-10 rounded-full flex items-center justify-center text-black disabled:opacity-40" style={{ backgroundColor: accent }}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
      </div>
    );
  }

  const npcContacts = home.npc_contacts || [];
  const npcConvos = home.npc_convos || [];
  const empty = !(home.convos || []).length && !(home.groups || []).length && !npcConvos.length;

  return (
    <div className="p-3 flex flex-col gap-1.5">
      <div className="grid grid-cols-2 gap-1 p-1 rounded-xl bg-white/5 border border-white/10">
        {[["chats", "Chats"], ["people", "People"]].map(([id, label]) => (
          <button key={id} data-testid={`messages-tab-${id}`} onClick={() => setTab(id)}
                  className={`py-2 rounded-lg font-mono text-[9px] uppercase tracking-[0.2em] transition-colors ${tab === id ? "text-black" : "text-white/55 hover:text-white"}`}
                  style={tab === id ? { backgroundColor: accent } : {}}>
            {label}
          </button>
        ))}
      </div>
      {tab === "people" ? (
        <PeopleApp demo={demo} serverId={serverId} token={token} accent={accent} sfx={sfx} />
      ) : (
        <>
          <div className="flex gap-2">
            <button data-testid="msg-new" onClick={() => { setPicking(!picking); setGrouping(false); }} className="flex-1 py-2.5 rounded-xl font-mono text-[10px] uppercase tracking-[0.2em] border border-dashed border-white/20 text-white/70 hover:text-white hover:border-white/40 transition-colors">+ New message</button>
            <button data-testid="msg-new-group" onClick={() => { setGrouping(!grouping); setPicking(false); }} className="flex-1 py-2.5 rounded-xl font-mono text-[10px] uppercase tracking-[0.2em] border border-dashed border-white/20 text-white/70 hover:text-white hover:border-white/40 transition-colors flex items-center justify-center gap-1.5"><Users className="w-3.5 h-3.5" /> New group</button>
          </div>
          {picking && (
            <div className="border border-white/10 rounded-xl bg-white/5 p-2 flex flex-col gap-1">
              {contacts.length === 0 && npcContacts.length === 0 && <p className="font-mono text-[10px] text-white/40 p-2">No contacts yet.</p>}
              {contacts.map((c) => (
                <button key={c.playercode} data-testid={`pick-${c.playercode}`} onClick={() => { setPicking(false); setView({ kind: "dm", pc: c.playercode, name: c.name }); }} className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg hover:bg-white/10 text-left transition-colors">
                  <UserRound className="w-4 h-4" style={{ color: accent }} /><span className="font-mono text-[11px] text-white">{c.name}</span><span className="font-mono text-[8px] text-white/40 ml-auto">#{c.playercode}</span>
                </button>
              ))}
              {npcContacts.length > 0 && <p className="font-mono text-[8px] uppercase tracking-[0.2em] text-white/35 px-3 pt-2">People you swapped numbers with</p>}
              {npcContacts.map((c) => (
                <button key={c.ref} data-testid={`pick-npc-${c.ref}`} onClick={() => { setPicking(false); setView({ kind: "npc", ref: c.ref, name: c.name }); }} className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg hover:bg-white/10 text-left transition-colors">
                  <MessageSquare className="w-4 h-4" style={{ color: accent }} /><span className="font-mono text-[11px] text-white">{c.name}</span><span className="font-mono text-[8px] text-white/40 ml-auto">{c.occupation}</span>
                </button>
              ))}
            </div>
          )}
          {grouping && (
            <div className="border border-white/10 rounded-xl bg-white/5 p-3 flex flex-col gap-2.5">
              <input data-testid="group-name" value={gName} maxLength={40} onChange={(e) => setGName(e.target.value)} placeholder="Group name — e.g. BPD Night Shift" className="bg-white/10 border border-white/15 rounded-xl px-3 py-2.5 font-mono text-[11px] text-white focus:outline-none" />
              {contacts.map((c) => (
                <button key={c.playercode} data-testid={`gm-${c.playercode}`} onClick={() => setGMembers((m) => m.includes(c.playercode) ? m.filter((x) => x !== c.playercode) : [...m, c.playercode])}
                  className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-left transition-colors border ${gMembers.includes(c.playercode) ? "bg-white/10" : "border-transparent hover:bg-white/5"}`} style={{ borderColor: gMembers.includes(c.playercode) ? accent : undefined }}>
                  <UserRound className="w-4 h-4" style={{ color: accent }} /><span className="font-mono text-[11px] text-white">{c.name}</span>
                  {gMembers.includes(c.playercode) && <CheckCheck className="w-3.5 h-3.5 ml-auto" style={{ color: accent }} />}
                </button>
              ))}
              <button data-testid="group-create" disabled={busy || gName.trim().length < 2 || gMembers.length < 1} onClick={createGroup} className="py-2.5 rounded-xl font-mono text-[10px] uppercase tracking-[0.2em] font-bold text-black disabled:opacity-40" style={{ backgroundColor: accent }}>
                {busy ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : `Create group (${gMembers.length + 1})`}
              </button>
            </div>
          )}
          {(home.groups || []).map((g) => (
            <button key={g.id} data-testid={`group-${g.id.slice(0, 6)}`} onClick={() => setView({ kind: "group", id: g.id, name: g.name, membersCount: g.members.length })} className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-white/5 text-left transition-colors border border-transparent hover:border-white/10">
              <span className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center shrink-0"><Users className="w-5 h-5" style={{ color: accent }} /></span>
              <span className="flex-1 min-w-0"><span className="font-sans font-semibold text-sm text-white block truncate">{g.name}</span><span className="block font-mono text-[10px] text-white/50 truncate">{g.last || `${g.members.length} members`}</span></span>
            </button>
          ))}
          {npcConvos.map((c) => (
            <button key={c.ref} data-testid={`npc-convo-${c.ref}`} onClick={() => setView({ kind: "npc", ref: c.ref, name: c.name })} className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-white/5 text-left transition-colors border border-transparent hover:border-white/10">
              <span className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center shrink-0"><MessageSquare className="w-5 h-5" style={{ color: accent }} /></span>
              <span className="flex-1 min-w-0">
                <span className="flex items-center gap-2"><span className="font-sans font-semibold text-sm text-white truncate">{c.name}</span>{c.checkin && <span data-testid={`npc-kind-${c.ref}`} className="font-mono text-[7px] uppercase tracking-[0.14em] border border-white/15 text-white/45 px-1.5 py-0.5 rounded-full">{c.kind || "check-in"}</span>}{c.unread > 0 && <span data-testid={`npc-unread-${c.ref}`} className="min-w-4 h-4 px-1 rounded-full bg-red-500 text-white font-mono text-[9px] font-bold flex items-center justify-center">{c.unread}</span>}</span>
                <span className="block font-mono text-[10px] text-white/50 truncate">{c.last}</span>
              </span>
              <span className="font-mono text-[8px] text-white/40 shrink-0">{String(c.at).slice(11, 16)}</span>
            </button>
          ))}
          {(home.convos || []).map((c) => (
            <button key={c.peer_pc} data-testid={`convo-${c.peer_pc}`} onClick={() => setView({ kind: "dm", pc: c.peer_pc, name: c.peer_name })} className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-white/5 text-left transition-colors border border-transparent hover:border-white/10">
              <span className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center shrink-0"><UserRound className="w-5 h-5" style={{ color: accent }} /></span>
              <span className="flex-1 min-w-0">
                <span className="flex items-center gap-2"><span className="font-sans font-semibold text-sm text-white truncate">{c.peer_name}</span>{c.unread > 0 && <span className="min-w-4 h-4 px-1 rounded-full bg-red-500 text-white font-mono text-[9px] font-bold flex items-center justify-center">{c.unread}</span>}</span>
                <span className="block font-mono text-[10px] text-white/50 truncate">{c.last}</span>
              </span>
              <span className="font-mono text-[8px] text-white/40 shrink-0">{String(c.at).slice(11, 16)}</span>
            </button>
          ))}
          {empty && !picking && !grouping && <p className="font-mono text-[10px] text-white/40 text-center mt-8">No conversations — text a citizen or swap numbers in the city.</p>}
        </>
      )}
    </div>
  );
}
